import { FC, useState, useMemo } from 'react'

import { IPortalAuthData } from 'components/contentspace/sessionCookie'
import {
  ContentSpaceAccessMode,
  IContentSpaceGroupPermissions,
  IPublicSsoConfig,
} from 'components/contentspace/types'
import Tabs from 'components/uiKit/Tabs'
import TabPanel from 'components/uiKit/Tabs/TabPanel'
import { isPortalMode } from 'routes/history'
import { t } from 'services/Translation'

import * as s from './AccessGate.module.scss'
import logoWhite from './assets/logo-white.svg'
import { AnonymousAccess } from './forms/AnonymousAccess'
import { EmailVerificationForm } from './forms/EmailVerificationForm'
import { PartaLogin } from './forms/PartaLogin'
import { SsoLogin } from './forms/SsoLogin'
import { UserInfoForm } from './forms/UserInfoForm'

interface IAccessGateProps {
  permissions: IContentSpaceGroupPermissions
  companyId: string
  portalId: string
  portalName: string
  portalDescription?: string
  ssoConfigs?: IPublicSsoConfig[]
  portalSsoAuth?: IPortalAuthData | null
  onAccessGranted: (userInfo: { email?: string; name?: string; verified: boolean } | null) => void
}

const ACCESS_MODE_ORDER: ContentSpaceAccessMode[] = [
  'SSO',
  'PARTA_USER',
  'EMAIL_VERIFIED',
  'WITH_USER_INFO',
  'ANONYMOUS',
]

const ACCESS_MODE_LABELS: Record<ContentSpaceAccessMode, string> = {
  EMAIL_VERIFIED: 'Email Verification',
  WITH_USER_INFO: 'Your Details',
  ANONYMOUS: 'Anonymous',
  PARTA_USER: 'Login',
  SSO: 'Single Sign-On',
}

export const AccessGate: FC<IAccessGateProps> = ({
  permissions,
  companyId,
  portalId,
  portalName,
  portalDescription,
  ssoConfigs,
  portalSsoAuth,
  onAccessGranted,
}) => {
  const sortedAccessModes = useMemo(() => {
    const modes = permissions?.accessModes || ['ANONYMOUS']
    return ACCESS_MODE_ORDER.filter((mode) => modes.includes(mode))
  }, [permissions?.accessModes])

  const ssoAlreadyAuthenticated = !!portalSsoAuth?.ssoAuthenticated

  const initialMode = useMemo<ContentSpaceAccessMode>(() => {
    if (ssoAlreadyAuthenticated) {
      const nextMode = sortedAccessModes.find((mode) => mode !== 'SSO')
      if (nextMode) {
        return nextMode
      }
    }
    return sortedAccessModes[0] || 'ANONYMOUS'
  }, [sortedAccessModes, ssoAlreadyAuthenticated])

  const [activeMode, setActiveMode] = useState<ContentSpaceAccessMode>(initialMode)

  const renderForm = (mode: ContentSpaceAccessMode) => {
    switch (mode) {
      case 'ANONYMOUS':
        return <AnonymousAccess onContinue={() => onAccessGranted(null)} />

      case 'WITH_USER_INFO':
        return (
          <UserInfoForm
            initialEmail={portalSsoAuth?.email}
            initialName={portalSsoAuth?.name}
            onSubmit={(email, name) => onAccessGranted({ email, name, verified: false })}
            ssoFlow={ssoAlreadyAuthenticated}
          />
        )

      case 'EMAIL_VERIFIED':
        return (
          <EmailVerificationForm
            companyId={companyId}
            initialEmail={portalSsoAuth?.email}
            onVerified={(email) => onAccessGranted({ email, verified: true })}
            portalId={portalId}
          />
        )

      case 'PARTA_USER':
        return (
          <PartaLogin
            onContinue={(email, name) => onAccessGranted({ email, name, verified: true })}
          />
        )

      case 'SSO':
        return (
          <SsoLogin
            companyId={companyId}
            portalId={portalId}
            returnUrl={window.location.href}
            ssoConfigs={ssoConfigs || []}
          />
        )

      default:
        return <div>Access denied</div>
    }
  }

  const hasMutipleModes = sortedAccessModes.length > 1

  return (
    <div className={s.root}>
      <div className={s.card}>
        <div className={s.portalName}>{portalName}</div>
        {portalDescription && <div className={s.portalDescription}>{portalDescription}</div>}

        {hasMutipleModes ? (
          <Tabs
            activeKey={activeMode}
            destroyInactiveTabPane={false}
            name='accessModeTabs'
            onChange={setActiveMode}
            styleType='base'
            tabContentStyle={{ paddingTop: '24px' }}
            tabPosition='tabCenter'
          >
            {sortedAccessModes.map((mode) => (
              <TabPanel key={mode} tabKey={mode} title={ACCESS_MODE_LABELS[mode]}>
                {renderForm(mode)}
              </TabPanel>
            ))}
          </Tabs>
        ) : (
          renderForm(sortedAccessModes[0])
        )}
      </div>
      {activeMode !== 'SSO' && !isPortalMode && (
        <div className={s.footer}>
          <div className={s.footerLogo}>
            <img alt='logo' src={logoWhite} />
          </div>
          <span>{t('page.auth.enterSystem')}</span>{' '}
          <a
            className={s.link}
            href={window.PLATFORM.termsOfService}
            rel='noreferrer'
            target='_blank'
          >
            {t('page.auth.terms')}
          </a>
          ,{' '}
          <a
            className={s.link}
            href={window.PLATFORM.privacyPolicy}
            rel='noreferrer'
            target='_blank'
          >
            {t('page.auth.privacyPolicy')}.
          </a>
        </div>
      )}
    </div>
  )
}
